/**
 * humanityUiSounds — minimal click-only sound helper.
 *
 * Two MP3s, preloaded once, played on intentional UI actions only:
 *   • playUiClick()         — soft futuristic click for any button/tap
 *   • playEntityActivation() — once-only Jarvis-style activation when the
 *                              entity conversation starts
 *
 * No Web Audio API, no oscillators, no background music, no hover sounds,
 * no pointer-move sounds. Failures are caught silently — these are
 * decorative, never critical.
 */

const UI_CLICK_SRC = '/audio/ui-click.mp3';
const ENTITY_ACTIVATION_SRC = '/audio/mycelium-communication-4.mp3';
const ENTITY_WAKE_SRC = '/audio/mycelium-interacting-3.mp3';

const UI_CLICK_VOLUME = 0.16;
const ENTITY_ACTIVATION_VOLUME = 0.22;
const ENTITY_WAKE_VOLUME = 0.15;

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

const cache = {
  click: null,
  activation: null,
  wake: null,
};

function getBuffer(name) {
  if (!isBrowser) return null;
  if (name === 'click') {
    if (!cache.click) {
      cache.click = new Audio(UI_CLICK_SRC);
      cache.click.preload = 'auto';
      cache.click.volume = UI_CLICK_VOLUME;
    }
    return cache.click;
  }
  if (name === 'activation') {
    if (!cache.activation) {
      cache.activation = new Audio(ENTITY_ACTIVATION_SRC);
      cache.activation.preload = 'auto';
      cache.activation.volume = ENTITY_ACTIVATION_VOLUME;
    }
    return cache.activation;
  }
  if (name === 'wake') {
    if (!cache.wake) {
      cache.wake = new Audio(ENTITY_WAKE_SRC);
      cache.wake.preload = 'auto';
      cache.wake.volume = ENTITY_WAKE_VOLUME;
    }
    return cache.wake;
  }
  return null;
}

/**
 * Play a short sound by name. Clones the cached Audio element so rapid
 * repeated clicks layer cleanly instead of stomping each other.
 */
function play(name) {
  if (!isBrowser) return;
  const base = getBuffer(name);
  if (!base) return;
  try {
    // Clone keeps the cached HEAD download warm while letting overlapping
    // taps spawn their own playback head. cloneNode(false) is cheap.
    const inst = base.cloneNode(false);
    inst.volume = base.volume;
    const p = inst.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => { /* silent — browser blocked or asset missing */ });
    }
  } catch {
    /* ignore */
  }
}

export function playUiClick() { play('click'); }
export function playEntityActivation() { play('activation'); }
export function playEntityWake() { play('wake'); }

/**
 * React hook — preloads all assets on mount so the first click never pays
 * the network latency cost. Returns stable callbacks.
 */
import { useEffect } from 'react';

export function useHumanityUiSounds() {
  useEffect(() => {
    getBuffer('click');
    getBuffer('activation');
    getBuffer('wake');
  }, []);
  return { playUiClick, playEntityActivation, playEntityWake };
}
